import React, { useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import {
  BodyText,
  Checkbox,
  TextLinkifyTags,
  theme,
  useResponsive,
} from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

export const SweetpassCheckoutCheckbox = (
  props: SweetpassCheckoutCheckboxProps,
) => {
  const { termsAndConditions, hasAcceptedTerms, handleAcceptTerms } = props;

  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  const links: React.ComponentProps<typeof TextLinkifyTags>['links'] = useMemo(
    () => [
      {
        tag: 'sweetpassProgramAgreement',
        description: t('sweetpass.checkout.terms.sweetpass-program-agreement'),
        href: 'https://www.sweetgreen.com/sweetpass-program-agreement',
        newTab: true,
        underline: true,
      },
      {
        tag: 'hello@sweetgreen.com',
        description: 'hello@sweetgreen.com',
        href: 'mailto:hello@sweetgreen.com',
        newTab: true,
        underline: true,
      },
    ],
    [t],
  );

  const onCheckTerms = useCallback(
    (checked?: boolean) => {
      handleAcceptTerms(checked ?? false);
    },
    [handleAcceptTerms],
  );

  const checkboxStyle = [
    styles.checkbox,
    match([styles.checkboxXS, styles.checkboxSM]),
  ];

  return (
    <Checkbox
      testID="sweetpass.checkout.accept"
      style={checkboxStyle}
      checked={hasAcceptedTerms}
      onChange={onCheckTerms}
    >
      <BodyText size={4}>
        <TextLinkifyTags size={4} links={links}>
          {termsAndConditions}
        </TextLinkifyTags>
      </BodyText>
    </Checkbox>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassCheckoutCheckboxProps = Readonly<{
  termsAndConditions: string;
  hasFreeTrial: boolean;
  hasAcceptedTerms: boolean;
  handleAcceptTerms: (hasAcceptedTerms: boolean) => void;
}>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  checkbox: {
    alignItems: 'flex-start',
  },
  checkboxXS: {
    paddingBottom: theme.spacing['10'],
  },
  checkboxSM: {
    paddingBottom: theme.spacing['6'],
  },
});
